import React from 'react';
import { Link } from 'react-router-dom';
import SeoPageLayout, { useSeo, seoTextStyles as s } from '../../components/seo/SeoPageLayout';

export default function InstagramVideoDimensions() {
  useSeo(
    'Instagram Video Dimensions Guide (Reels, Feed, Stories) — Waxify',
    'The correct video dimensions and aspect ratios for Instagram Reels, feed posts, and Stories, plus how to export the right size with Waxify.'
  );

  return (
    <SeoPageLayout>
      <h1 className={s.h1}>Instagram Video Dimensions</h1>
      <p className={s.lead}>
        Instagram supports a few different video shapes depending on where the video
        appears, and posting the wrong ratio usually means it gets cropped or
        letterboxed. Here's what actually fits where.
      </p>

      <h2 className={s.h2}>Reels — 9:16 (1080 × 1920px)</h2>
      <p className={s.p}>
        Reels are full-screen vertical video, the same shape as TikTok and YouTube
        Shorts. This is the format most likely to get pushed by the algorithm right
        now, so if you're only making one version of a spinning record video, make it
        this one. In Waxify, pick the portrait/9:16 export preset.
      </p>

      <h2 className={s.h2}>Feed posts — 1:1 or 4:5</h2>
      <p className={s.p}>
        Square (1:1) is the safest bet for a regular feed post since it displays fully
        without cropping in the grid view. 4:5 (slightly taller than wide) is also
        supported and uses a bit more vertical screen space in the feed itself. A
        square export from Waxify works cleanly for both.
      </p>

      <h2 className={s.h2}>Stories — 9:16 (1080 × 1920px)</h2>
      <p className={s.p}>
        Same ratio as Reels, full vertical screen. If you've already exported a 9:16
        video for Reels, it'll work for Stories without any changes.
      </p>

      <h2 className={s.h2}>What happens if you post the wrong ratio</h2>
      <p className={s.p}>
        Instagram doesn't reject the wrong shape — it just crops or pads it to fit the
        container. A square video posted to Reels gets pillarboxed with black bars on
        the sides; a vertical video posted to the feed either gets cropped top and
        bottom or shown smaller with bars. Neither looks great, and cropping in
        particular can cut off part of your disc or label art if it's near the edge of
        the frame.
      </p>

      <h2 className={s.h2}>Picking the right export in Waxify</h2>
      <ul className={s.ul}>
        <li>Posting to Reels or Stories: choose the portrait (9:16) preset</li>
        <li>Posting to your main feed: choose the square (1:1) preset</li>
        <li>Cross-posting to YouTube: the landscape (16:9) preset matches standard YouTube video</li>
      </ul>
      <p className={s.p}>
        Because the disc sits centered in the frame regardless of aspect ratio, you can
        set up your art and audio once and just switch the export preset for each
        platform — no need to reposition anything.
      </p>

      <p className={s.p}>
        See the full process in our <Link to="/how-to-make-a-spinning-record-video" className="text-orange-600 hover:underline font-medium">step-by-step guide</Link>, or <Link to="/" className="text-orange-600 hover:underline font-medium">open the editor</Link> to start exporting.
      </p>
    </SeoPageLayout>
  );
}
